import { type FormEvent, useState } from 'react';
import { api } from '../api';
import { ErrorNote } from '../components/ui';
import { useSession } from '../session';

const EMAIL_TYPES = [
  { value: 'otp', label: 'Login codes and OTPs' },
  { value: 'account', label: 'Sign-up and password emails' },
  { value: 'receipts', label: 'Orders, invoices, receipts' },
  { value: 'notifications', label: 'Alerts and notifications' },
  { value: 'marketing', label: 'Newsletters or marketing' },
];

const VOLUMES = [
  { value: 'under_1k', label: 'Under 1,000', hint: 'Just starting' },
  { value: '1k_10k', label: '1,000 – 10,000', hint: 'A small app' },
  { value: '10k_100k', label: '10,000 – 1 lakh', hint: 'Growing' },
  { value: 'over_100k', label: 'Over 1 lakh', hint: 'At scale' },
];

/** First screen for a new team: a few questions we use to review beta accounts. */
export function Welcome() {
  const { me, team, reload, logout } = useSession();
  const [company, setCompany] = useState(team.name);
  const [website, setWebsite] = useState('');
  const [types, setTypes] = useState<string[]>([]);
  const [volume, setVolume] = useState('');
  const [details, setDetails] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const onlyMarketing = types.length > 0 && types.every((t) => t === 'marketing');

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (types.length === 0) return setError('Choose at least one kind of email.');
    if (!volume) return setError('Choose how many emails you expect to send.');
    setBusy(true);
    setError(null);
    try {
      await api('PUT', '/team/onboarding', {
        company,
        website: website || null,
        emailTypes: types,
        monthlyVolume: volume,
        details: details || null,
      });
      await reload();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <div className="auth-card wide">
        <div className="auth-brand">
          <img src="/vaartix-icon.svg" alt="" />
          vaartix
        </div>
        <div className="auth-box">
          <h1>Welcome to Vaartix</h1>
          <p>
            Tell us a little about what you'll send. It takes a minute and helps us keep your emails
            out of spam folders.
          </p>
          <form onSubmit={submit}>
            <div className="field">
              <label htmlFor="company">Company or project name</label>
              <input
                id="company"
                className="input"
                required
                maxLength={80}
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="website">Website or app link</label>
              <input
                id="website"
                className="input"
                inputMode="url"
                placeholder="acme.in"
                maxLength={200}
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>
            <fieldset className="field">
              <legend className="label">What will you send?</legend>
              <div className="checks-grid">
                {EMAIL_TYPES.map((t) => (
                  <label key={t.value}>
                    <input
                      type="checkbox"
                      checked={types.includes(t.value)}
                      onChange={(e) =>
                        setTypes((list) =>
                          e.target.checked ? [...list, t.value] : list.filter((v) => v !== t.value),
                        )
                      }
                    />
                    {t.label}
                  </label>
                ))}
              </div>
              {types.includes('marketing') ? (
                <span className={`hint${onlyMarketing ? ' warn' : ''}`}>
                  Vaartix is for transactional email; newsletters and marketing aren't supported
                  yet.{onlyMarketing ? ' We may not be the right fit for now.' : ''}
                </span>
              ) : null}
            </fieldset>
            <fieldset className="field">
              <legend className="label">Emails per month</legend>
              <div className="radio-cards">
                {VOLUMES.map((v) => (
                  <label key={v.value}>
                    <input
                      type="radio"
                      name="volume"
                      value={v.value}
                      checked={volume === v.value}
                      onChange={() => setVolume(v.value)}
                    />
                    <strong>{v.label}</strong>
                    <span>{v.hint}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="field">
              <label htmlFor="details">
                Anything else? <span className="dim">(optional)</span>
              </label>
              <textarea
                id="details"
                className="input textarea"
                rows={3}
                maxLength={1000}
                placeholder="For example: moving from another provider, sending from Laravel"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
              />
            </div>
            <ErrorNote error={error} />
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Continue to dashboard'}
            </button>
          </form>
        </div>
        <p className="auth-foot">
          Signed in as {me.user.email} ·{' '}
          <button type="button" className="link-btn" onClick={() => void logout()}>
            Log out
          </button>
        </p>
      </div>
    </div>
  );
}
